import { PaginationMetadata, SortCriterion } from "../../../types/responses";
import { SiteDto } from "../../../types/blastDtos";
import { SubscriptionDto } from "../../../types/dto";
import { isMockMode, postBlast, simulateDelay } from "../../api";

// Shape returned to React consumers (the server-side DataTable reads `results`).
// NB: the backend wire returns the rows under `sites` (kept that way so the
// Paradigm desktop reads .Sites); this layer maps that onto `results`.
export interface GetSitesForUserResult {
	results: Array<SiteDto>;
	subscriptions: Array<SubscriptionDto>;
	paginationMetadata: PaginationMetadata;
}

export const getSitesForUser = ({
	orgId,
	filterCriteria,
	sortCriteria,
	pageNo,
	pageSize,
	search,
	take,
}: {
	orgId: string;
	filterCriteria?: Record<string, object>;
	sortCriteria?: Array<SortCriterion>;
	pageNo?: number;
	pageSize?: number;
	// Convenience free-text search (site picker); mapped to filterCriteria.base
	// when filterCriteria is not supplied.
	search?: string;
	take?: number;
}): Promise<GetSitesForUserResult> => {
	// Prefer an explicit filterCriteria; otherwise fall back to a `search` term.
	const effectiveFilterCriteria =
		filterCriteria ?? (search ? { base: search } : undefined);

	if (isMockMode()) {
		const mockSites: Array<SiteDto> = [
			{
				id: "3fa85f64-5717-4562-b5gh-2c963f66afa6",
				name: "Test Name",
				subscription: {
					id: "3fa85f64-5717-4562-b5gh-2c963f66afa6",
					name: "Test Name",
					subscriberOrgId: "3fa85f64-5717-4562-b5gh-2c963f66afa6",
					isDeleted: true,
					isEnabled: true,
					expirationDateUtc: "2023-08-10T02:04:27.595Z",
					lastUpdatedDateUtc: "2023-08-10T02:04:27.595Z",
				},
			},
		] as unknown as Array<SiteDto>;

		return simulateDelay(50, () => ({
			results: mockSites,
			subscriptions: [] as Array<SubscriptionDto>,
			paginationMetadata: {
				pageNo: 1,
				pageSize: mockSites.length,
				totalPages: 1,
				totalAvailable: mockSites.length,
				fetchedCount: mockSites.length,
			},
		})) as Promise<GetSitesForUserResult>;
	}

	const body: Record<string, unknown> = { orgId };
	if (effectiveFilterCriteria) body.filterCriteria = effectiveFilterCriteria;
	if (sortCriteria) body.sortCriteria = sortCriteria;
	if (pageNo != null) body.pageNo = pageNo;
	if (pageSize != null) body.pageSize = pageSize;
	if (take != null) body.take = take;

	return postBlast(`sites/getforuser`, body).then((response: any) => {
		// Backend returns the rows under `sites` (desktop reads .Sites); map to
		// `results` for the server-side DataTable. Pagination fields stay nested.
		const sites: Array<SiteDto> = response?.sites ?? [];
		const paginationMetadata = response?.paginationMetadata ?? {
			pageNo: pageNo ?? 1,
			pageSize: pageSize ?? sites.length,
			totalPages: 1,
			totalAvailable: sites.length,
			fetchedCount: sites.length,
		};
		return {
			results: sites,
			subscriptions: response?.subscriptions ?? [],
			paginationMetadata: {
				...paginationMetadata,
				totalAvailable:
					paginationMetadata.totalAvailable ?? sites.length,
				fetchedCount: paginationMetadata.fetchedCount ?? sites.length,
			},
		} as GetSitesForUserResult;
	});
};
